/**
 * Formatting utility functions
 */

/**
 * Format price to currency string
 */
export const formatPrice = (
  price: string | number,
  currency: string = 'EUR'
): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice)) return '0';

  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice);
};

/**
 * Format mileage/number with thousand separators
 */
export const formatMileage = (mileage: number | string): string => {
  const numMileage =
    typeof mileage === 'string' ? parseFloat(mileage) : mileage;
  if (isNaN(numMileage)) return '0';

  return new Intl.NumberFormat('de-DE').format(numMileage);
};

/**
 * Format number with thousand separators
 */
export const formatNumber = (value: number | string): string => {
  return formatMileage(value);
};

/**
 * Format date to readable string
 */
export const formatDate = (
  date: string | Date,
  locale: string = 'de-DE'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj);
};

/**
 * Format date to short string (DD.MM.YYYY)
 */
export const formatDateShort = (
  date: string | Date,
  locale: string = 'de-DE'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dateObj);
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDateShort(dateObj);
};
